import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiUsers, FiLogIn, FiSend, FiX, FiCheck,
  FiLink, FiMessageSquare, FiAlertCircle, FiShield
} from "react-icons/fi";
import { collection, onSnapshot, getDocs } from "firebase/firestore";
import { db } from "./firebase";
import Navbar from "./Navbar";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import emailjs from "emailjs-com";
import "react-toastify/dist/ReactToastify.css";

export default function RealtimeGaDashboard() {
  const [users, setUsers] = useState([]);
  const [user, setUser] = useState(null);
  const [message, setMessage] = useState("");
  const [fileUrl, setFileUrl] = useState("");
  const [userCount, setUserCount] = useState(0);
  const [loginCount, setLoginCount] = useState(0);
  const [showConfirm, setShowConfirm] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [sending, setSending] = useState(false);
  const navigate = useNavigate();
  const adminEmail = "karanksxxx@gmail.com";

  // ─── Colors ─────────────────────────────────────────────
  const c = {
    bg: "#050508",
    bgCard: "rgba(15, 15, 25, 0.6)",
    border: "rgba(255, 255, 255, 0.06)",
    borderLight: "rgba(255, 255, 255, 0.1)",
    text: "#f0f0f5",
    textMuted: "#8888a0",
    textDark: "#555570",
    primary: "#6366f1",
    primaryLight: "rgba(99, 102, 241, 0.1)",
    primaryBorder: "rgba(99, 102, 241, 0.2)",
    accent: "#a855f7",
    green: "#22c55e",
    greenLight: "rgba(34, 197, 94, 0.1)",
    greenBorder: "rgba(34, 197, 94, 0.2)",
    red: "#f43f5e",
    redLight: "rgba(244, 63, 94, 0.1)",
    redBorder: "rgba(244, 63, 94, 0.2)",
    gradient: "linear-gradient(135deg, #6366f1, #a855f7)",
  };

  // ─── Data Fetch ─────────────────────────────────────────
  useEffect(() => {
    const currentUser = JSON.parse(localStorage.getItem("user"));
    setUser(currentUser);

    getDocs(collection(db, "Register")).then((snap) => {
      const fetchedUsers = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setUsers(fetchedUsers);
      setSelectedUsers(fetchedUsers.map((u) => u.Email));
      setUserCount(snap.size);
    });

    const unsub = onSnapshot(collection(db, "Login"), (snap) => {
      setLoginCount(snap.size);
    });

    return () => unsub();
  }, []);

  // ─── Selection ──────────────────────────────────────────
  const toggleUserSelection = (email) => {
    setSelectedUsers((prev) =>
      prev.includes(email) ? prev.filter((e) => e !== email) : [...prev, email]
    );
  };

  const toggleAllUsers = () => {
    if (selectedUsers.length === users.length) setSelectedUsers([]);
    else setSelectedUsers(users.map((u) => u.Email));
  };

  // ─── Send Emails ────────────────────────────────────────
  const sendEmails = async () => {
    if (!selectedUsers.length) return toast.warn("No users selected");
    if (!message.trim()) return toast.warn("Message cannot be empty");

    setSending(true);
    try {
      await Promise.all(
        selectedUsers.map((email) =>
          emailjs.send(
            "service_5aiin0d",
            "template_ltoem0u",
            { to_email: email, message, fileUrl },
            "xjmUCpr_q6Qh9Gmt6"
          )
        )
      );
      toast.success(`Emails sent to ${selectedUsers.length} users! 🎉`);
      setShowConfirm(false);
      setMessage("");
      setFileUrl("");
    } catch {
      toast.error("Failed to send emails");
    } finally {
      setSending(false);
    }
  };

  // ─── Access Denied ──────────────────────────────────────
  if (!user || user.Email !== adminEmail) {
    return (
      <div style={styles.accessDenied}>
        <div style={styles.accessContent}>
          <div style={styles.accessIcon}><FiAlertCircle /></div>
          <h2 style={styles.accessTitle}>Access Denied</h2>
          <p style={styles.accessText}>You don't have permission to view this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar
        theme="dark"
        toastStyle={{
          background: "#0d0d14",
          border: "1px solid rgba(255,255,255,0.1)",
          color: "#f0f0f5",
        }}
      />

      {/* ─── Hero ─── */}
      <section style={styles.hero}>
        <div style={styles.heroBg} />
        <motion.div
          style={styles.heroContent}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <span style={styles.tag}><FiShield /> ADMIN</span>
          <h1 style={styles.heroTitle}>
            Notification <span style={styles.gradientText}>Dashboard</span>
          </h1>
          <p style={styles.heroSub}>Send notifications and manage user communications.</p>
        </motion.div>
      </section>

      {/* ─── Main ─── */}
      <main style={styles.main}>
        <div style={styles.container}>
          {/* Stats */}
          <div style={styles.statsGrid}>
            <motion.div
              style={styles.statCard}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <div style={{ ...styles.statIcon, color: c.primary }}><FiUsers /></div>
              <div>
                <span style={styles.statValue}>{userCount}</span>
                <span style={styles.statLabel}>Total Users</span>
              </div>
            </motion.div>

            <motion.div
              style={styles.statCard}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <div style={{ ...styles.statIcon, color: c.accent }}><FiLogIn /></div>
              <div>
                <span style={styles.statValue}>{loginCount}</span>
                <span style={styles.statLabel}>Total Logins</span>
              </div>
            </motion.div>

            <motion.div
              style={styles.statCard}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <div style={{ ...styles.statIcon, color: c.green }}><FiCheck /></div>
              <div>
                <span style={styles.statValue}>{selectedUsers.length}</span>
                <span style={styles.statLabel}>Selected</span>
              </div>
            </motion.div>
          </div>

          {/* Notification Form */}
          <motion.div
            style={styles.card}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <div style={styles.cardHeader}>
              <span style={styles.formTag}><FiSend /> SEND NOTIFICATION</span>
              <h2 style={styles.cardTitle}>Compose <span style={styles.gradientText}>Message</span></h2>
            </div>

            <div style={styles.form}>
              <div style={styles.formGroup}>
                <label style={styles.label}><FiMessageSquare /> Message</label>
                <textarea
                  style={styles.textarea}
                  placeholder="Write your notification message..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={5}
                />
                <span style={styles.charCount}>{message.length} characters</span>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}><FiLink /> File URL (Optional)</label>
                <input
                  style={styles.input}
                  placeholder="https://..."
                  value={fileUrl}
                  onChange={(e) => setFileUrl(e.target.value)}
                />
              </div>

              <motion.button
                style={styles.primaryBtn}
                onClick={() => setShowConfirm(true)}
                whileHover={{ scale: 1.02, y: -2 }}
                whileTap={{ scale: 0.98 }}
              >
                <FiSend /> Preview & Send
              </motion.button>
            </div>
          </motion.div>
        </div>
      </main>

      {/* ─── Modal ─── */}
      <AnimatePresence>
        {showConfirm && (
          <motion.div
            style={styles.overlay}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowConfirm(false)}
          >
            <motion.div
              style={styles.modal}
              initial={{ scale: 0.9, y: 30, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.9, y: 30, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={styles.modalHeader}>
                <div>
                  <h2 style={styles.modalTitle}>Select Recipients</h2>
                  <p style={styles.modalSub}>{selectedUsers.length} of {users.length} users selected</p>
                </div>
                <motion.button
                  style={styles.closeBtn}
                  onClick={() => setShowConfirm(false)}
                  whileHover={{ rotate: 90 }}
                >
                  <FiX />
                </motion.button>
              </div>

              <div style={styles.modalDivider} />

              {/* Message Preview */}
              <div style={styles.previewBox}>
                <span style={styles.previewLabel}>Message Preview</span>
                <p style={styles.previewText}>{message || "No message"}</p>
                {fileUrl && (
                  <p style={styles.previewLink}><FiLink /> {fileUrl}</p>
                )}
              </div>

              {/* Toggle All */}
              <motion.button
                style={styles.toggleBtn}
                onClick={toggleAllUsers}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {selectedUsers.length === users.length ? "Deselect All" : "Select All"}
              </motion.button>

              {/* User List */}
              <div style={styles.userList}>
                {users.map((u) => (
                  <motion.label
                    key={u.id}
                    style={{
                      ...styles.userItem,
                      background: selectedUsers.includes(u.Email) ? c.primaryLight : "transparent",
                      borderColor: selectedUsers.includes(u.Email) ? c.primaryBorder : c.border,
                    }}
                    whileHover={{ background: "rgba(255,255,255,0.03)" }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedUsers.includes(u.Email)}
                      onChange={() => toggleUserSelection(u.Email)}
                      style={styles.checkbox}
                    />
                    <div style={styles.userInfo}>
                      <span style={styles.userName}>{u.Name || "Unknown"}</span>
                      <span style={styles.userEmail}>{u.Email}</span>
                    </div>
                    {selectedUsers.includes(u.Email) && (
                      <FiCheck style={{ color: c.primary }} />
                    )}
                  </motion.label>
                ))}
              </div>

              {/* Actions */}
              <div style={styles.actions}>
                <motion.button
                  style={styles.primaryBtn}
                  onClick={sendEmails}
                  disabled={sending}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {sending ? "Sending..." : `Send to ${selectedUsers.length} Users`}
                </motion.button>
                <motion.button
                  style={styles.cancelBtn}
                  onClick={() => setShowConfirm(false)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Cancel
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <Navbar />
    </div>
  );
}

// ─── Styles ───────────────────────────────────────────────────
const styles = {
  page: {
    background: "#050508",
    minHeight: "100vh",
    color: "#f0f0f5",
    fontFamily: "'Inter', 'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif",
    overflowX: "hidden",
  },

  // Access Denied
  accessDenied: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100vh",
    background: "#050508",
  },
  accessContent: { textAlign: "center" },
  accessIcon: {
    width: "80px",
    height: "80px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "rgba(244, 63, 94, 0.1)",
    border: "2px solid rgba(244, 63, 94, 0.2)",
    borderRadius: "24px",
    fontSize: "2rem",
    color: "#f43f5e",
    margin: "0 auto 20px",
  },
  accessTitle: { fontSize: "2rem", fontWeight: "700", marginBottom: "8px" },
  accessText: { color: "#8888a0" },

  // Hero
  hero: {
    position: "relative",
    padding: "clamp(140px, 18vh, 200px) 0 clamp(30px, 5vh, 60px)",
    overflow: "hidden",
  },
  heroBg: {
    position: "absolute",
    inset: 0,
    background: "radial-gradient(ellipse at 50% 50%, rgba(99, 102, 241, 0.08) 0%, transparent 60%)",
    pointerEvents: "none",
  },
  heroContent: {
    position: "relative",
    zIndex: 1,
    textAlign: "center",
    maxWidth: "600px",
    margin: "0 auto",
    padding: "0 clamp(20px, 4vw, 60px)",
  },
  tag: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: "0.75rem",
    letterSpacing: "3px",
    textTransform: "uppercase",
    color: "#f43f5e",
    marginBottom: "16px",
    padding: "6px 16px",
    background: "rgba(244, 63, 94, 0.1)",
    border: "1px solid rgba(244, 63, 94, 0.2)",
    borderRadius: "100px",
  },
  heroTitle: {
    fontSize: "clamp(2.5rem, 6vw, 3.5rem)",
    fontWeight: "800",
    letterSpacing: "-0.04em",
    lineHeight: "1.1",
    marginBottom: "12px",
  },
  heroSub: { fontSize: "1rem", color: "#8888a0", lineHeight: "1.7" },
  gradientText: {
    background: "linear-gradient(135deg, #6366f1, #a855f7)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
  },

  // Main
  main: {
    position: "relative",
    zIndex: 1,
    paddingBottom: "100px",
  },
  container: {
    maxWidth: "700px",
    margin: "0 auto",
    padding: "0 clamp(16px, 4vw, 60px)",
  },

  // Stats
  statsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: "16px",
    marginBottom: "24px",
  },
  statCard: {
    display: "flex",
    alignItems: "center",
    gap: "14px",
    padding: "20px",
    background: "rgba(15, 15, 25, 0.6)",
    border: "1px solid rgba(255, 255, 255, 0.06)",
    borderRadius: "16px",
    backdropFilter: "blur(20px)",
  },
  statIcon: {
    width: "42px",
    height: "42px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "rgba(99, 102, 241, 0.1)",
    borderRadius: "12px",
    fontSize: "1.1rem",
  },
  statValue: {
    display: "block",
    fontSize: "1.5rem",
    fontWeight: "800",
    color: "#f0f0f5",
    letterSpacing: "-0.03em",
  },
  statLabel: {
    display: "block",
    fontSize: "0.75rem",
    color: "#8888a0",
    fontWeight: "500",
  },

  // Card
  card: {
    background: "rgba(15, 15, 25, 0.6)",
    border: "1px solid rgba(255, 255, 255, 0.06)",
    borderRadius: "24px",
    padding: "32px",
    backdropFilter: "blur(20px)",
    position: "relative",
    overflow: "hidden",
  },
  cardHeader: { marginBottom: "24px" },
  formTag: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: "0.75rem",
    letterSpacing: "2px",
    textTransform: "uppercase",
    color: "#6366f1",
    padding: "6px 14px",
    background: "rgba(99, 102, 241, 0.1)",
    border: "1px solid rgba(99, 102, 241, 0.2)",
    borderRadius: "100px",
    marginBottom: "12px",
  },
  cardTitle: { fontSize: "1.5rem", fontWeight: "700" },

  // Form
  form: { display: "flex", flexDirection: "column", gap: "16px" },
  formGroup: { display: "flex", flexDirection: "column", gap: "8px" },
  label: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "0.8rem",
    fontWeight: "600",
    color: "#8888a0",
    textTransform: "uppercase",
    letterSpacing: "1px",
  },
  input: {
    width: "100%",
    padding: "14px 18px",
    background: "rgba(255, 255, 255, 0.05)",
    border: "1px solid rgba(255, 255, 255, 0.08)",
    borderRadius: "12px",
    color: "#f0f0f5",
    fontSize: "0.95rem",
    fontFamily: "inherit",
    outline: "none",
    transition: "all 0.3s ease",
    boxSizing: "border-box",
  },
  textarea: {
    width: "100%",
    padding: "14px 18px",
    background: "rgba(255, 255, 255, 0.05)",
    border: "1px solid rgba(255, 255, 255, 0.08)",
    borderRadius: "12px",
    color: "#f0f0f5",
    fontSize: "0.95rem",
    fontFamily: "inherit",
    outline: "none",
    transition: "all 0.3s ease",
    resize: "vertical",
    lineHeight: "1.7",
    boxSizing: "border-box",
  },
  charCount: {
    fontSize: "0.72rem",
    color: "#555570",
    textAlign: "right",
    fontFamily: "'JetBrains Mono', monospace",
  },

  // Buttons
  primaryBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    padding: "14px",
    background: "linear-gradient(135deg, #6366f1, #a855f7)",
    border: "none",
    borderRadius: "12px",
    color: "white",
    fontSize: "0.95rem",
    fontWeight: "600",
    fontFamily: "inherit",
    cursor: "pointer",
    boxShadow: "0 4px 20px rgba(99, 102, 241, 0.3)",
    transition: "all 0.3s ease",
  },
  cancelBtn: {
    padding: "14px",
    background: "rgba(255, 255, 255, 0.05)",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    borderRadius: "12px",
    color: "#8888a0",
    fontSize: "0.95rem",
    fontWeight: "600",
    fontFamily: "inherit",
    cursor: "pointer",
  },
  toggleBtn: {
    width: "100%",
    padding: "10px",
    background: "rgba(99, 102, 241, 0.1)",
    border: "1px solid rgba(99, 102, 241, 0.2)",
    borderRadius: "10px",
    color: "#6366f1",
    fontSize: "0.85rem",
    fontWeight: "600",
    fontFamily: "inherit",
    cursor: "pointer",
    marginBottom: "12px",
  },

  // Modal
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0, 0, 0, 0.75)",
    backdropFilter: "blur(12px)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 10000,
    padding: "24px",
  },
  modal: {
    width: "100%",
    maxWidth: "500px",
    maxHeight: "85vh",
    background: "#0d0d14",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    borderRadius: "24px",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    boxShadow: "0 24px 80px rgba(0, 0, 0, 0.6)",
  },
  modalHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    padding: "24px 24px 0",
  },
  modalTitle: { fontSize: "1.3rem", fontWeight: "700" },
  modalSub: { fontSize: "0.85rem", color: "#6366f1", marginTop: "4px" },
  closeBtn: {
    width: "36px",
    height: "36px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "rgba(255, 255, 255, 0.05)",
    border: "1px solid rgba(255, 255, 255, 0.08)",
    borderRadius: "10px",
    color: "#8888a0",
    fontSize: "1.1rem",
    cursor: "pointer",
  },
  modalDivider: {
    height: "1px",
    background: "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent)",
    margin: "16px 24px 0",
  },

  // Preview
  previewBox: {
    margin: "16px 24px",
    padding: "14px",
    background: "rgba(99, 102, 241, 0.05)",
    border: "1px solid rgba(99, 102, 241, 0.15)",
    borderRadius: "12px",
  },
  previewLabel: {
    display: "block",
    fontSize: "0.7rem",
    textTransform: "uppercase",
    letterSpacing: "1px",
    color: "#6366f1",
    fontWeight: "700",
    marginBottom: "6px",
  },
  previewText: { fontSize: "0.85rem", color: "#c0c0d0", lineHeight: "1.6", margin: 0 },
  previewLink: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    fontSize: "0.8rem",
    color: "#6366f1",
    marginTop: "8px",
  },

  // User list
  userList: {
    flex: 1,
    overflowY: "auto",
    padding: "0 24px",
    maxHeight: "200px",
  },
  userItem: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "10px 14px",
    marginBottom: "6px",
    borderRadius: "10px",
    border: "1px solid rgba(255, 255, 255, 0.06)",
    cursor: "pointer",
    transition: "all 0.2s ease",
  },
  checkbox: {
    width: "18px",
    height: "18px",
    accentColor: "#6366f1",
    cursor: "pointer",
  },
  userInfo: { flex: 1, minWidth: 0 },
  userName: {
    display: "block",
    fontSize: "0.85rem",
    fontWeight: "600",
    color: "#f0f0f5",
  },
  userEmail: {
    display: "block",
    fontSize: "0.75rem",
    color: "#8888a0",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  // Actions
  actions: {
    display: "flex",
    gap: "10px",
    padding: "16px 24px 24px",
  },
};
